import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { posService } from "../services/pos.service";

export const fetchPosList = createAsyncThunk("pos/fetchAll", async (_, thunkAPI) => {
  try {
    return await posService.getPosList();
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data?.message || "Failed to fetch POS list");
  }
});

export const fetchPosByDateRange = createAsyncThunk(
  "pos/fetchByDateRange",
  async (filter, thunkAPI) => {
    try {
      return await posService.getPosByDateRange(filter);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err?.response?.data?.message || "Failed to fetch POS list by date range"
      );
    }
  }
);

export const fetchPosById = createAsyncThunk("pos/fetchById", async (id, thunkAPI) => {
  try {
    return await posService.getPosById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data?.message || "Failed to fetch POS");
  }
});

export const addPos = createAsyncThunk("pos/add", async (pos, thunkAPI) => {
  try {
    return await posService.createPos(pos);
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data?.message || "Failed to create POS");
  }
});

export const editPos = createAsyncThunk("pos/edit", async ({ id, pos }, thunkAPI) => {
  try {
    return await posService.updatePos(id, pos);
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data?.message || "Failed to update POS");
  }
});

export const removePos = createAsyncThunk("pos/remove", async (id, thunkAPI) => {
  try {
    return await posService.deletePos(id);
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data?.message || "Failed to delete POS");
  }
});

export const closePosSession = createAsyncThunk("pos/close", async (id, thunkAPI) => {
  try {
    return await posService.closePos(id);
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data?.message || "Failed to close POS");
  }
});

const initialState = {
  posList: [],
  selectedPos: null,
  totalCount: 0,
  loading: {
    fetch: false,
    create: false,
    edit: false,
    delete: false,
    close: false,
    details: false,
  },
  error: null,
  showModal: false,
  showDeleteModal: false,
  showCloseModal: false,
};

const replacePosInList = (list, updatedPos) =>
  list.map((pos) => (pos.posId === updatedPos.posId ? updatedPos : pos));

const posSlice = createSlice({
  name: "pos",
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
      state.error = null;
    },
    closeModal: (state) => {
      state.showModal = false;
      state.selectedPos = null;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
      state.error = null;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    openCloseModal: (state) => {
      state.showCloseModal = true;
      state.error = null;
    },
    closeCloseModal: (state) => {
      state.showCloseModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosList.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchPosList.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.posList = action.payload.items ?? [];
        state.totalCount = action.payload.totalCount ?? 0;
      })
      .addCase(fetchPosList.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload;
      })

      .addCase(fetchPosByDateRange.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchPosByDateRange.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.posList = action.payload.items ?? [];
        state.totalCount = action.payload.totalCount ?? 0;
      })
      .addCase(fetchPosByDateRange.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = action.payload;
      })

      .addCase(fetchPosById.pending, (state) => {
        state.loading.details = true;
        state.error = null;
      })
      .addCase(fetchPosById.fulfilled, (state, action) => {
        state.loading.details = false;
        state.selectedPos = action.payload;
      })
      .addCase(fetchPosById.rejected, (state, action) => {
        state.loading.details = false;
        state.error = action.payload;
      })

      .addCase(addPos.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(addPos.fulfilled, (state, action) => {
        state.loading.create = false;
        if (action.payload) {
          state.posList.unshift(action.payload);
          state.totalCount += 1;
        }
        state.showModal = false;
      })
      .addCase(addPos.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload;
      })

      .addCase(editPos.pending, (state) => {
        state.loading.edit = true;
        state.error = null;
      })
      .addCase(editPos.fulfilled, (state, action) => {
        state.loading.edit = false;
        if (action.payload) {
          state.posList = replacePosInList(state.posList, action.payload);
          if (state.selectedPos?.posId === action.payload.posId) {
            state.selectedPos = action.payload;
          }
        }
        state.showModal = false;
      })
      .addCase(editPos.rejected, (state, action) => {
        state.loading.edit = false;
        state.error = action.payload;
      })

      .addCase(removePos.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(removePos.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.posList = state.posList.filter((pos) => pos.posId !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.showDeleteModal = false;
      })
      .addCase(removePos.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload;
      })

      .addCase(closePosSession.pending, (state) => {
        state.loading.close = true;
        state.error = null;
      })
      .addCase(closePosSession.fulfilled, (state, action) => {
        state.loading.close = false;
        if (action.payload) {
          state.posList = replacePosInList(state.posList, action.payload);
          if (state.selectedPos?.posId === action.payload.posId) {
            state.selectedPos = action.payload;
          }
        }
        state.showCloseModal = false;
      })
      .addCase(closePosSession.rejected, (state, action) => {
        state.loading.close = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  openCloseModal,
  closeCloseModal,
  clearError,
} = posSlice.actions;

export default posSlice.reducer;